import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import NumberInput from '@rio-cloud/rio-uikit/NumberInput';
import RangeSlider from '@rio-cloud/rio-uikit/RangeSlider';
import debounce from 'lodash/debounce';

import type { RangeValue } from '@/features/brakes/hooks/useBrakesQueryParams';

interface RangeInputWithSliderProps {
    min: number;
    max: number;
    onRangeChange: (range: RangeValue) => void;
    range: RangeValue;
    unit: string;
    disableDebounce?: boolean;
    title: string;
}

export const formatRange = (range: RangeValue, unit: string) => {
    const { from, to } = range;
    if (typeof from !== 'number' && typeof to !== 'number') {
        return '';
    }
    const sanitizedUnit = unit ? ` ${unit}` : '';
    const fromValue = typeof from === 'number' ? from.toLocaleString() : '';
    const toValue = typeof to === 'number' ? to.toLocaleString() : '';
    return `${fromValue}${sanitizedUnit} → ${toValue}${sanitizedUnit}`;
};

const RangeInputWithSlider: React.FC<RangeInputWithSliderProps> = ({
    min = 0,
    max = 100,
    onRangeChange,
    range,
    unit,
    disableDebounce = false,
    title,
}) => {
    const [isDropdownOpened, setIsDropdownOpened] = useState(false);
    const [internalState, setInternalState] = useState<RangeValue>(range);

    useEffect(() => {
        setInternalState(range);
    }, [range]);

    const evaluateRange = (from: number, to: number): RangeValue => {
        return {
            from: from === min ? undefined : from,
            to: to === max ? undefined : to,
        };
    };

    const dropdownTitle = useMemo(() => {
        const { from, to } = range;
        if ((!from && !to) || (from === min && to === max)) {
            return <span className="placeholder">{formatRange({ from: min, to: max }, unit)}</span>;
        }
        return <span className="placeholder">{formatRange(range, unit)}</span>;
    }, [range.to, range.from, min, max, unit]);

    const debouncedOnToChange = useCallback(
        disableDebounce
            ? (from: number, to: number) => {
                  onRangeChange(evaluateRange(from, to));
              }
            : debounce(
                  (from: number, to: number) => {
                      onRangeChange(evaluateRange(from, to));
                  },
                  200,
                  { maxWait: 1200 },
              ),
        [disableDebounce, range, onRangeChange],
    );
    const leftValue = range.from || min;
    const rightValue = range.to || max;
    return (
        <div className="display-flex flex-column min-width-200">
            <div className="display-flex align-content-start gap-10">
                <span className="text-color-highlight-light margin-bottom-10">
                    <FormattedMessage id={title} />
                </span>
            </div>
            <ButtonDropdown
                className={'range-input-with-slider align-top select'}
                title={dropdownTitle}
                open={isDropdownOpened}
                onOpen={() => setIsDropdownOpened(true)}
                onClose={() => setIsDropdownOpened(false)}
                customDropdown={
                    <>
                        <li className="slider">
                            <RangeSlider
                                key={`${leftValue}-${rightValue}`}
                                leftValue={leftValue}
                                rightValue={rightValue}
                                minValue={min}
                                maxValue={max}
                                step={1}
                                onChange={(from: number, to: number) => {
                                    setInternalState(evaluateRange(from, to));
                                }}
                                onDragEnd={(from: number, to: number) => {
                                    onRangeChange(evaluateRange(from, to));
                                }}
                            />
                        </li>
                        <li className="range">
                            <InputRange range={internalState} min={min} max={max} onRangeChange={debouncedOnToChange} />
                        </li>
                        <li className="btn-toolbar">
                            <button
                                type="button"
                                className="btn btn-primary pull-right"
                                onClick={() => setIsDropdownOpened(false)}
                            >
                                <FormattedMessage id="intl-msg:vehicle-diagnostics.button.close" />
                            </button>
                            <button
                                onClick={() => {
                                    setInternalState({ from: undefined, to: undefined });
                                    debouncedOnToChange(min, max);
                                }}
                                type="button"
                                className="btn btn-default pull-right"
                                disabled={(!range.from && !range.to) || (range.from === min && range.to === max)}
                            >
                                <FormattedMessage id="intl-msg:vehicle-diagnostics.button.reset" />
                            </button>
                        </li>
                    </>
                }
            />
        </div>
    );
};
type InputRangeProps = {
    range: RangeValue;
    min: number;
    max: number;
    onRangeChange: (from: number, to: number) => void;
};
const InputRange = ({ min, max, range, onRangeChange }: InputRangeProps) => {
    const { from, to } = range;
    return (
        <div className="display-grid gap-10 grid-cols-1-xs grid-cols-2-sm">
            <div className="input-group">
                <div className="form-control-feedback-wrapper">
                    <NumberInput
                        min={min}
                        max={to ?? max}
                        step={1}
                        value={from}
                        onChange={(value) => {
                            onRangeChange(value ?? from ?? min, to ?? max);
                        }}
                    />
                </div>
            </div>
            <div className="input-group">
                <div className="form-control-feedback-wrapper">
                    <NumberInput
                        min={from ?? min}
                        max={max}
                        step={1}
                        value={to}
                        onChange={(value) => {
                            onRangeChange(from ?? min, value ?? to ?? max);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default RangeInputWithSlider;
